import React, { useEffect, useMemo, useState } from "react";
import {
  MessageCircle,
  RotateCcw,
  Save,
  Eye,
  Copy,
  Check,
  Loader2,
  AlertCircle,
  Zap,
  Hand,
  Info,
} from "lucide-react";
import {
  fetchWaTemplates,
  updateWaTemplate,
  resetWaTemplate,
  previewWaTemplate,
  renderTemplate,
} from "../../lib/api";
import { toast } from "../../hooks/use-toast";

/**
 * WhatsApp templates admin.
 * Templates are grouped into two categories:
 *   • Automatico — sent by the system on a booking/PMS event.
 *   • Manuale — the admin composes on-demand (campaigns, generic replies).
 * For each template we show a clear "Quando viene inviato" banner and a
 * placeholder table with the meaning of every {variable}.
 */
export default function MessagesEditor() {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeKey, setActiveKey] = useState(null);
  const [draft, setDraft] = useState("");
  const [preview, setPreview] = useState("");
  const [saving, setSaving] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const list = await fetchWaTemplates();
      setTemplates(list);
      if (!activeKey && list.length) setActiveKey(list[0].key);
    } catch (e) {
      toast({ title: "Errore", description: "Impossibile caricare i template" });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []); // eslint-disable-line

  const active = useMemo(
    () => templates.find((t) => t.key === activeKey) || null,
    [templates, activeKey],
  );

  // Reset the draft when the user switches template.
  useEffect(() => {
    if (active) setDraft(active.body);
  }, [active]);

  // Debounced live preview via the backend so the sample context matches
  // exactly what the real endpoint will render.
  useEffect(() => {
    if (!activeKey) return;
    const t = setTimeout(async () => {
      setPreviewLoading(true);
      try {
        const r = await previewWaTemplate(activeKey, draft);
        setPreview(r.rendered);
      } catch {
        setPreview(renderTemplate(draft));
      } finally {
        setPreviewLoading(false);
      }
    }, 350);
    return () => clearTimeout(t);
  }, [activeKey, draft]);

  const dirty = active && draft !== active.body;

  const save = async () => {
    if (!active || !dirty) return;
    setSaving(true);
    try {
      const updated = await updateWaTemplate(active.key, draft);
      setTemplates((prev) => prev.map((t) => (t.key === updated.key ? updated : t)));
      toast({ title: "Template salvato" });
    } catch (e) {
      toast({ title: "Errore salvataggio", description: e?.response?.data?.detail || e.message });
    } finally {
      setSaving(false);
    }
  };

  const reset = async () => {
    if (!active) return;
    if (!confirm(`Ripristinare il testo predefinito di "${active.label}"? Le tue modifiche andranno perse.`))
      return;
    try {
      const updated = await resetWaTemplate(active.key);
      setTemplates((prev) => prev.map((t) => (t.key === updated.key ? updated : t)));
      setDraft(updated.body);
      toast({ title: "Template ripristinato" });
    } catch (e) {
      toast({ title: "Errore ripristino", description: e?.response?.data?.detail || e.message });
    }
  };

  const insertPlaceholder = (ph) => {
    const el = document.getElementById("wa-template-textarea");
    if (!el) {
      setDraft((d) => d + `{${ph}}`);
      return;
    }
    const start = el.selectionStart;
    const end = el.selectionEnd;
    const token = `{${ph}}`;
    const next = draft.slice(0, start) + token + draft.slice(end);
    setDraft(next);
    requestAnimationFrame(() => {
      el.focus();
      const pos = start + token.length;
      el.setSelectionRange(pos, pos);
    });
  };

  // Split templates by category so the sidebar can render two sections.
  const grouped = useMemo(() => {
    const auto = templates.filter((t) => t.category === "auto");
    const manual = templates.filter((t) => t.category !== "auto");
    return { auto, manual };
  }, [templates]);

  if (loading) {
    return (
      <div className="py-16 flex justify-center" data-testid="wa-templates-loading">
        <Loader2 className="h-6 w-6 animate-spin text-[#4a1c17]" />
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto space-y-6" data-testid="wa-templates-editor">
      {/* Header banner */}
      <div className="rounded-2xl bg-gradient-to-br from-[#2b1410] to-[#4a1c17] text-[#f7ebdf] p-5 flex flex-wrap items-center gap-4">
        <div className="h-11 w-11 rounded-lg bg-[#f7ebdf]/10 flex items-center justify-center">
          <MessageCircle className="h-5 w-5" />
        </div>
        <div className="flex-1 min-w-[240px]">
          <h2 className="font-serif text-2xl font-bold">Messaggi WhatsApp</h2>
          <p className="text-sm text-[#f7ebdf]/75 mt-0.5">
            Personalizza il testo dei messaggi WhatsApp che parte dai bottoni dell'admin. Ogni
            template mostra <b>quando viene inviato</b> e le variabili disponibili.
          </p>
        </div>
        <div className="flex flex-wrap gap-2 text-[11px]">
          <span className="inline-flex items-center gap-1 rounded-full bg-emerald-500/20 text-emerald-100 border border-emerald-400/30 px-2.5 py-1">
            <Zap className="h-3 w-3" /> Automatico
          </span>
          <span className="inline-flex items-center gap-1 rounded-full bg-amber-500/20 text-amber-100 border border-amber-400/30 px-2.5 py-1">
            <Hand className="h-3 w-3" /> Manuale
          </span>
        </div>
      </div>

      <div className="grid md:grid-cols-[260px_1fr] gap-5">
        {/* Left list of templates, grouped */}
        <aside className="space-y-4 self-start md:sticky md:top-24">
          <TemplateGroup
            title="Automatici"
            icon={<Zap className="h-3.5 w-3.5" />}
            tone="emerald"
            items={grouped.auto}
            activeKey={activeKey}
            onSelect={setActiveKey}
          />
          <TemplateGroup
            title="Manuali"
            icon={<Hand className="h-3.5 w-3.5" />}
            tone="amber"
            items={grouped.manual}
            activeKey={activeKey}
            onSelect={setActiveKey}
          />
        </aside>

        {/* Editor */}
        {active && (
          <section className="space-y-4 min-w-0">
            {/* Header card: label + save/reset buttons */}
            <div className="bg-white border border-[#e8dcc8] rounded-xl p-4">
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <div className="font-serif text-xl font-bold text-[#2b1410]">{active.label}</div>
                    <CategoryBadge category={active.category} />
                  </div>
                  <p className="text-sm text-[#7c5a4e] mt-1">{active.description}</p>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <button
                    type="button"
                    onClick={reset}
                    disabled={active.is_default}
                    data-testid="wa-template-reset"
                    className="inline-flex items-center gap-1.5 rounded-full border border-[#e8dcc8] bg-white text-[#2b1410] px-3 py-1.5 text-xs font-semibold hover:bg-[#f7ebdf] disabled:opacity-40 disabled:cursor-not-allowed"
                    title={active.is_default ? "Nessuna modifica da ripristinare" : "Ripristina il testo predefinito"}
                  >
                    <RotateCcw className="h-3.5 w-3.5" /> Default
                  </button>
                  <button
                    type="button"
                    onClick={save}
                    disabled={!dirty || saving}
                    data-testid="wa-template-save"
                    className="inline-flex items-center gap-1.5 rounded-full bg-[#4a1c17] hover:bg-[#2b1410] text-[#f7ebdf] px-3 py-1.5 text-xs font-semibold disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />}
                    Salva
                  </button>
                </div>
              </div>
            </div>

            {/* Trigger banner: WHEN this template is sent */}
            {active.trigger && (
              <div
                className="rounded-xl border-l-4 border-l-[#4a1c17] bg-[#fbf6ef] border border-[#e8dcc8] p-4 flex gap-3"
                data-testid="wa-template-trigger"
              >
                <Info className="h-5 w-5 text-[#4a1c17] shrink-0 mt-0.5" />
                <div className="min-w-0">
                  <div className="text-[11px] uppercase tracking-wide font-semibold text-[#4a1c17] mb-1">
                    Quando viene inviato
                  </div>
                  <p className="text-sm text-[#2b1410] leading-relaxed">{active.trigger}</p>
                </div>
              </div>
            )}

            {/* Placeholders help table */}
            {active.placeholders?.length > 0 && (
              <div className="bg-white border border-[#e8dcc8] rounded-xl p-4">
                <div className="flex items-center justify-between mb-3">
                  <div className="text-[11px] uppercase tracking-wide font-semibold text-[#7c5a4e]">
                    Variabili disponibili — clicca per inserire
                  </div>
                </div>
                <div className="grid sm:grid-cols-2 gap-2">
                  {active.placeholders.map((ph) => (
                    <button
                      key={ph}
                      type="button"
                      onClick={() => insertPlaceholder(ph)}
                      data-testid={`wa-template-ph-${ph}`}
                      className="text-left rounded-lg border border-[#e8dcc8] bg-[#fbf6ef] hover:bg-[#f7ebdf] hover:border-[#4a1c17]/40 px-3 py-2 transition group"
                    >
                      <div className="text-xs font-mono font-semibold text-[#4a1c17] group-hover:underline">
                        {`{${ph}}`}
                      </div>
                      {active.placeholder_help?.[ph] && (
                        <div className="text-[11px] text-[#7c5a4e] leading-snug mt-0.5">
                          {active.placeholder_help[ph]}
                        </div>
                      )}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Textarea + preview side by side on desktop */}
            <div className="grid lg:grid-cols-2 gap-4">
              <div className="bg-white border border-[#e8dcc8] rounded-xl p-3">
                <div className="flex items-center justify-between mb-2">
                  <label className="text-xs uppercase tracking-wide font-semibold text-[#7c5a4e]">
                    Testo del messaggio
                  </label>
                  <span className="text-[11px] text-[#7c5a4e]">{draft.length} / 4000</span>
                </div>
                <textarea
                  id="wa-template-textarea"
                  data-testid="wa-template-body"
                  value={draft}
                  onChange={(e) => setDraft(e.target.value)}
                  rows={16}
                  className="w-full rounded-md border border-[#e8dcc8] px-3 py-2 text-sm font-mono bg-[#fbf6ef] focus:outline-none focus:ring-2 focus:ring-[#4a1c17]/20 resize-y"
                  spellCheck={false}
                />
                <div className="mt-2 text-[11px] text-[#7c5a4e] flex items-start gap-1.5">
                  <AlertCircle className="h-3.5 w-3.5 shrink-0 mt-0.5" />
                  <span>
                    Usa <code>*grassetto*</code>, <code>_corsivo_</code>, emoji ✅ come su WhatsApp.
                    I placeholder mancanti diventano vuoti.
                  </span>
                </div>
              </div>

              <div className="bg-[#e5ddd5] border border-[#d8ccc0] rounded-xl p-3 min-w-0">
                <div className="flex items-center justify-between mb-2">
                  <label className="text-xs uppercase tracking-wide font-semibold text-[#7c5a4e] flex items-center gap-1">
                    <Eye className="h-3.5 w-3.5" /> Anteprima (dati esempio)
                  </label>
                  <CopyBtn text={preview} />
                </div>
                <div
                  className="bg-[#dcf8c6] rounded-lg p-3 text-sm whitespace-pre-wrap break-words shadow-sm max-h-[440px] overflow-y-auto"
                  data-testid="wa-template-preview"
                >
                  {previewLoading ? (
                    <span className="text-[#7c5a4e] italic">Aggiornamento…</span>
                  ) : (
                    formatWhatsAppPreview(preview)
                  )}
                </div>
              </div>
            </div>
          </section>
        )}
      </div>
    </div>
  );
}

// --------------------------- Small subcomponents ---------------------------

function TemplateGroup({ title, icon, tone, items, activeKey, onSelect }) {
  if (!items || items.length === 0) return null;
  const toneClasses = {
    emerald: "text-emerald-800 bg-emerald-50 border-emerald-200",
    amber: "text-amber-800 bg-amber-50 border-amber-200",
  };
  return (
    <div className="space-y-1.5">
      <div
        className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-0.5 text-[10px] uppercase tracking-wide font-semibold ${toneClasses[tone] || toneClasses.emerald}`}
      >
        {icon}
        {title}
      </div>
      <div className="space-y-1">
        {items.map((t) => (
          <button
            key={t.key}
            type="button"
            onClick={() => onSelect(t.key)}
            data-testid={`wa-template-tab-${t.key}`}
            className={`w-full text-left px-3 py-2 rounded-lg border transition text-sm ${
              t.key === activeKey
                ? "bg-[#4a1c17] text-[#f7ebdf] border-[#4a1c17]"
                : "bg-white border-[#e8dcc8] text-[#2b1410] hover:border-[#4a1c17]/40"
            }`}
          >
            <div className="font-semibold leading-tight">{t.label}</div>
            {!t.is_default && (
              <div
                className={`text-[10px] mt-0.5 uppercase tracking-wide ${
                  t.key === activeKey ? "text-[#f7ebdf]/70" : "text-emerald-700"
                }`}
              >
                Modificato
              </div>
            )}
          </button>
        ))}
      </div>
    </div>
  );
}

function CategoryBadge({ category }) {
  if (category === "auto") {
    return (
      <span
        className="inline-flex items-center gap-1 rounded-full bg-emerald-100 text-emerald-800 border border-emerald-200 px-2 py-0.5 text-[10px] uppercase tracking-wide font-semibold"
        data-testid="wa-template-category-auto"
      >
        <Zap className="h-3 w-3" /> Automatico
      </span>
    );
  }
  return (
    <span
      className="inline-flex items-center gap-1 rounded-full bg-amber-100 text-amber-800 border border-amber-200 px-2 py-0.5 text-[10px] uppercase tracking-wide font-semibold"
      data-testid="wa-template-category-manual"
    >
      <Hand className="h-3 w-3" /> Manuale
    </span>
  );
}

// Render *bold* and _italic_ WA-style markers to help the admin see what
// the message will look like. Preview-only; the actual message stays raw.
function formatWhatsAppPreview(text) {
  if (!text) return null;
  const parts = [];
  const re = /(\*[^*\n]+\*|_[^_\n]+_)/g;
  let last = 0;
  let m;
  let i = 0;
  while ((m = re.exec(text)) !== null) {
    if (m.index > last) parts.push(text.slice(last, m.index));
    const tok = m[0];
    if (tok.startsWith("*")) parts.push(<strong key={i++}>{tok.slice(1, -1)}</strong>);
    else parts.push(<em key={i++}>{tok.slice(1, -1)}</em>);
    last = m.index + tok.length;
  }
  if (last < text.length) parts.push(text.slice(last));
  return parts;
}

function CopyBtn({ text }) {
  const [ok, setOk] = useState(false);
  return (
    <button
      type="button"
      onClick={async () => {
        try {
          await navigator.clipboard.writeText(text || "");
          setOk(true);
          setTimeout(() => setOk(false), 1200);
        } catch {}
      }}
      className="inline-flex items-center gap-1 text-[11px] text-[#7c5a4e] hover:text-[#2b1410]"
      data-testid="wa-template-copy"
      title="Copia anteprima"
    >
      {ok ? <Check className="h-3.5 w-3.5 text-emerald-600" /> : <Copy className="h-3.5 w-3.5" />}
      {ok ? "Copiato" : "Copia"}
    </button>
  );
}
